import './PersuasionOutcomeChip.css';
import { DispatchCardDismiss } from './DispatchCardDismiss';

/**
 * V9.2.2 (2026-06-06) — PersuasionOutcomeChip.
 *
 * Pure-presentation render of a `force_persuasion_attempt` dispatch
 * action. The LLM picks target_npc_id + request_summary + optional EF
 * modifiers; this chip surfaces the SETUP. The PERSUASION CHART lookup
 * (target's WIL tier × persuader's QR → N/?/Y) is resolved downstream;
 * pass the resolved cell via the `outcome` prop when known.
 *
 * Schema 1-to-1 with `force_persuasion_attempt` tool args in
 * briefing_room_backend/routes/dispatch.py:413-468:
 *   - target_npc_id (string, required)
 *   - request_summary (string, required)
 *   - ef_modifier_ids: array of {trivial_request, minor_request,
 *     major_request, deadly_request, appropriate_emotion}
 *   - rationale: GM voice-line ≤30 words (optional)
 *
 * Outcome cells per seed/tables/persuasion_chart.json:
 *   N = No (red), ? = Undecided / GM-arbitrated (amber), Y = Yes (green).
 */

export type PersuasionEfModifierId =
  | 'trivial_request'
  | 'minor_request'
  | 'major_request'
  | 'deadly_request'
  | 'appropriate_emotion';

export type PersuasionOutcomeCell = 'N' | '?' | 'Y';

export interface PersuasionOutcomeChipProps {
  targetNpcId: string;
  /** Humanized NPC display name. Defaults to a titlecase of targetNpcId. */
  targetNpcDisplay?: string;
  requestSummary: string;
  efModifierIds?: PersuasionEfModifierId[];
  rationale?: string;
  /** Resolved chart cell (PERSUASION CHART lookup). Omit → pending state. */
  outcome?: PersuasionOutcomeCell | null;
  /** V9.2.x.3: when provided, renders a ✕ dismiss button (top-right). */
  onDismiss?: () => void;
  testidPrefix?: string;
}

const EF_MODIFIER_LABEL: Record<PersuasionEfModifierId, string> = {
  trivial_request: 'Trivial request',
  minor_request: 'Minor request',
  major_request: 'Major request',
  deadly_request: 'Deadly request',
  appropriate_emotion: 'Appropriate emotion',
};

const OUTCOME_LABEL: Record<PersuasionOutcomeCell, string> = {
  N: 'No',
  '?': 'Undecided',
  Y: 'Yes',
};

function defaultDisplayName(id: string): string {
  return id
    .split('_')
    .map((part) => part.length === 0 ? part : part[0].toUpperCase() + part.slice(1))
    .join(' ');
}

export function PersuasionOutcomeChip({
  targetNpcId,
  targetNpcDisplay,
  requestSummary,
  efModifierIds,
  rationale,
  outcome,
  onDismiss,
  testidPrefix = 'br-persuasion-chip',
}: PersuasionOutcomeChipProps) {
  const display = targetNpcDisplay ?? defaultDisplayName(targetNpcId);
  const hasModifiers = efModifierIds !== undefined && efModifierIds.length > 0;
  const hasOutcome = outcome === 'N' || outcome === '?' || outcome === 'Y';

  return (
    <aside
      className="br-persuasion-chip"
      data-testid={testidPrefix}
      data-target-npc-id={targetNpcId}
      data-outcome={hasOutcome ? outcome : 'pending'}
    >
      <DispatchCardDismiss onDismiss={onDismiss} testidPrefix={testidPrefix} />
      <header className="br-persuasion-chip-header">
        <span className="br-persuasion-chip-label">Persuading</span>
        <span
          className="br-persuasion-chip-target"
          data-testid={`${testidPrefix}-target`}
        >
          {display}
        </span>
      </header>

      <p
        className="br-persuasion-chip-request"
        data-testid={`${testidPrefix}-request`}
      >
        {requestSummary}
      </p>

      {hasModifiers && (
        <ul
          className="br-persuasion-chip-modifiers"
          data-testid={`${testidPrefix}-modifiers`}
        >
          {efModifierIds!.map((mod) => (
            <li
              key={mod}
              className="br-persuasion-chip-modifier"
              data-testid={`${testidPrefix}-modifier-${mod}`}
              data-modifier-id={mod}
            >
              {EF_MODIFIER_LABEL[mod]}
            </li>
          ))}
        </ul>
      )}

      <div
        className="br-persuasion-chip-outcome"
        data-testid={`${testidPrefix}-outcome`}
        data-outcome-state={hasOutcome ? outcome : 'pending'}
      >
        {hasOutcome ? (
          <>
            <span className="br-persuasion-chip-outcome-cell">{outcome}</span>
            <span className="br-persuasion-chip-outcome-label">
              {OUTCOME_LABEL[outcome as PersuasionOutcomeCell]}
            </span>
          </>
        ) : (
          <span className="br-persuasion-chip-outcome-pending">Awaiting chart roll…</span>
        )}
      </div>

      {rationale !== undefined && rationale.length > 0 && (
        <p
          className="br-persuasion-chip-rationale"
          data-testid={`${testidPrefix}-rationale`}
        >
          {rationale}
        </p>
      )}
    </aside>
  );
}
